import { Component, ElementRef, ViewChild, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FlowItemModel } from '../../../shared/models/FlowItemModel';
import { ScoutingFlowService } from '../../../shared/services/scouting-flow.service';
import { ReportCardComponent } from '../../components/report-card/report-card.component';
import { NoteCardComponent } from '../../components/note-card/note-card.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule, MatLabel } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { GameGradesCardComponent } from '../../components/game-grades-card/game-grades-card.component';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

type TimeBucketKey =
  | 'SCOUTING_FLOW.TIME_BUCKETS.TODAY'
  | 'SCOUTING_FLOW.TIME_BUCKETS.YESTERDAY'
  | 'SCOUTING_FLOW.TIME_BUCKETS.THIS_WEEK'
  | 'SCOUTING_FLOW.TIME_BUCKETS.ONE_WEEK_AGO'
  | 'SCOUTING_FLOW.TIME_BUCKETS.TWO_WEEKS_AGO'
  | 'SCOUTING_FLOW.TIME_BUCKETS.ONE_MONTH_AGO'
  | 'SCOUTING_FLOW.TIME_BUCKETS.TWO_MONTHS_AGO'
  | 'SCOUTING_FLOW.TIME_BUCKETS.THREE_MONTHS_AGO'
  | 'SCOUTING_FLOW.TIME_BUCKETS.FOUR_MONTHS_AGO'
  | 'SCOUTING_FLOW.TIME_BUCKETS.FIVE_MONTHS_AGO'
  | 'SCOUTING_FLOW.TIME_BUCKETS.SIX_MONTHS_AGO'
  | 'SCOUTING_FLOW.TIME_BUCKETS.MORE_THAN_SIX_MONTHS_AGO';

@Component({
    selector: 'app-scouting-flow',
    imports: [CommonModule, FormsModule, ReportCardComponent, NoteCardComponent, MatProgressBarModule, GameGradesCardComponent, MatDividerModule, MatFormFieldModule, MatInputModule, MatLabel, MatButtonModule, MatIconModule, TranslatePipe],
    templateUrl: './scouting-flow.component.html',
    styleUrl: './scouting-flow.component.css'
})
export class ScoutingFlowComponent {
  _flowitems: FlowItemModel[];
  isLoading=signal(false);
  filterText = '';
  @ViewChild('listTopAnchor') listTopAnchor?: ElementRef<HTMLDivElement>;

  constructor(
    public flowService: ScoutingFlowService,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.loadDefaultFlow();
  }

  private loadDefaultFlow(): void {
    this.isLoading.set(true);
    this.flowService.getAllReports().subscribe(r => {
      this._flowitems = r;
      this.isLoading.set(false);
      this.scrollToListTop();
    });
  }

  onSearch(): void {
    const term = this.filterText?.trim() ?? '';
    if (term === '') {
      this.loadDefaultFlow();
      return;
    }
    this.isLoading.set(true);
    this.flowService.searchFlow(term).subscribe(r => {
      this._flowitems = r;
      this.isLoading.set(false);
      this.scrollToListTop();
    });
  }

  onClearSearch(): void {
    this.filterText = '';
    this.loadDefaultFlow();
  }

  getTimeBucket(item: FlowItemModel): TimeBucketKey {
    const date = item.DateCreated ? new Date(item.DateCreated) : null;
    if (!date) return 'SCOUTING_FLOW.TIME_BUCKETS.MORE_THAN_SIX_MONTHS_AGO';
    const msPerDay = 1000 * 60 * 60 * 24;
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const itemDayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayDiff = Math.floor((todayStart.getTime() - itemDayStart.getTime()) / msPerDay);

    if (dayDiff <= 0)   return 'SCOUTING_FLOW.TIME_BUCKETS.TODAY';
    if (dayDiff === 1)  return 'SCOUTING_FLOW.TIME_BUCKETS.YESTERDAY';
    if (dayDiff < 7)    return 'SCOUTING_FLOW.TIME_BUCKETS.THIS_WEEK';
    if (dayDiff < 14)   return 'SCOUTING_FLOW.TIME_BUCKETS.ONE_WEEK_AGO';
    if (dayDiff < 28)   return 'SCOUTING_FLOW.TIME_BUCKETS.TWO_WEEKS_AGO';
    if (dayDiff < 60)   return 'SCOUTING_FLOW.TIME_BUCKETS.ONE_MONTH_AGO';
    if (dayDiff < 90)   return 'SCOUTING_FLOW.TIME_BUCKETS.TWO_MONTHS_AGO';
    if (dayDiff < 120)  return 'SCOUTING_FLOW.TIME_BUCKETS.THREE_MONTHS_AGO';
    if (dayDiff < 150)  return 'SCOUTING_FLOW.TIME_BUCKETS.FOUR_MONTHS_AGO';
    if (dayDiff < 180)  return 'SCOUTING_FLOW.TIME_BUCKETS.FIVE_MONTHS_AGO';
    if (dayDiff < 210)  return 'SCOUTING_FLOW.TIME_BUCKETS.SIX_MONTHS_AGO';
    return 'SCOUTING_FLOW.TIME_BUCKETS.MORE_THAN_SIX_MONTHS_AGO';
  }

  getTimeBucketLabel(item: FlowItemModel): string {
    return this.translate.instant(this.getTimeBucket(item));
  }

  isFirstInBucket(index: number): boolean {
    if (index === 0) return true;
    return this.getTimeBucket(this._flowitems[index]) !== this.getTimeBucket(this._flowitems[index - 1]);
  }

  private scrollToListTop(): void {
    requestAnimationFrame(() => {
      this.listTopAnchor?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  }
}
